/**
 * Appearance & accessibility -- the person's own palette, modelled on
 * SigmaBuddy's Settings -> Appearance (SIGM-524) and its Accessibility Mode.
 *
 * Eden, 24 September 2026: "build/mimic Sigmabuddy's accessibility settings all
 * of it and add the matrix theme as one of the available options for UI
 * theming". So the shape is SigmaBuddy's, carried across deliberately:
 *
 * - **Presets are where you START FROM, not modes.** Picking one fills every
 *   colour below with its own values and you edit from there; a row's reset
 *   returns it to the preset's value, not to a factory default.
 * - **Edits are stored PER PRESET**, because an accent that reads on a dark
 *   ground is frequently unreadable on a light one.
 * - **Warn, never refuse.** `contrastVerdict` exists so the customiser can say a
 *   pairing is hard to read; nothing blocks a choice.
 * - **Accessibility Mode is two presets plus eleven switchable options**, and
 *   the options apply only while one of those presets is active -- exactly as in
 *   SigmaBuddy, where a panel of inert toggles on the other palettes was judged
 *   worse than no panel.
 *
 * ── What differs from SigmaBuddy, and why ─────────────────────────────
 *
 * TruckBuddy's palette is MONO (industry.css: "danger and emphasis share a hue,
 * so colour alone cannot..."). There are no success / error / warning tokens to
 * expose, so the customiser has eight colours and no Status group. Inventing
 * status colours here would add a meaning the rest of the app does not carry.
 *
 * The components read RAMPS (`--color-accent-100` ... `-900`, the neutral ramp),
 * not just a handful of core tokens, so a palette is applied as the full
 * derived set, mixing text into the ground so 100 is always nearest the ground.
 * That is what lets one formula serve a light palette and a dark one alike.
 *
 * The two base presets, Light and Dark, carry NO inline properties until the
 * person changes something: the stylesheet stays the authority for what ships,
 * and `BASE_TOKENS` below is only what the customiser shows and derives from
 * once an edit exists. `appearance.test.ts` reads industry.css and
 * industry-v2.css and fails if those values drift apart.
 *
 * Persistence is this browser's localStorage, like the light/dark choice before
 * it. SigmaBuddy saves the same blobs to the user's server-side preferences;
 * doing that here needs an `app_user` column and a route, which is a follow-up.
 */

export type Scheme = "light" | "dark";

export type AppearanceTokenKey =
  | "bg"
  | "surface"
  | "surface2"
  | "text"
  | "muted"
  | "border"
  | "accent"
  | "accent2";

export type AppearanceGroup = "Surfaces" | "Text & lines" | "Accent";

export const APPEARANCE_GROUPS: readonly AppearanceGroup[] = ["Surfaces", "Text & lines", "Accent"];

export interface AppearanceTokenDef {
  key: AppearanceTokenKey;
  label: string;
  help: string;
  group: AppearanceGroup;
  /** A field text sits on, so it is judged against the body text rather than against a panel. */
  surface?: boolean;
  /** Meant to sit close to its background (borders), so a low-contrast warning is noise. */
  subtle?: boolean;
  /**
   * The WCAG ratio this token is judged against. Defaults to 4.5:1, the AA
   * text target, when absent -- every token here paints text or a
   * text-adjacent control EXCEPT `accent2`, which `contrastLabel` below
   * explains.
   */
  minContrast?: number;
  /**
   * The words the warning uses to name `minContrast`, e.g. "readability
   * target" (the default, used when this is absent) or "target for tints".
   * Lives on the token rather than in the component that reads it, so the
   * row's warning names the target it actually judged against without the
   * component special-casing any one token by key.
   */
  contrastLabel?: string;
}

export const APPEARANCE_TOKENS: readonly AppearanceTokenDef[] = [
  {
    key: "bg",
    label: "Page background",
    group: "Surfaces",
    surface: true,
    help: "The field everything sits on, behind the panels.",
  },
  {
    key: "surface",
    label: "Panel",
    group: "Surfaces",
    surface: true,
    help: "Panels, cards and the navigation. Input wells are mixed from this and the page.",
  },
  {
    key: "surface2",
    label: "Raised panel",
    group: "Surfaces",
    surface: true,
    help: "Table header rows, menus, and anything that lifts off a panel.",
  },
  {
    key: "text",
    label: "Text",
    group: "Text & lines",
    help: "Body text. The grey ramp and hover tints are mixed from this, so they follow it.",
  },
  {
    key: "muted",
    label: "Muted text",
    group: "Text & lines",
    help: "Captions, hints and secondary detail.",
  },
  {
    key: "border",
    label: "Borders",
    group: "Text & lines",
    subtle: true,
    help: "Panel outlines, table rules and field borders.",
  },
  {
    key: "accent",
    label: "Accent",
    group: "Accent",
    help: "Links, focus rings, the primary button and selected rows -- the colour the app leans on.",
  },
  {
    key: "accent2",
    label: "Second accent",
    group: "Accent",
    help: "The quieter companion accent used for secondary tints.",
    // Eden, 24 September 2026: the second accent is a tint, judged at 3:1.
    // The base token has no `var(--color-accent-2)` use anywhere in the
    // app -- only its ramp's 100/800 steps, in `.tag-accent-2` -- so it is
    // never painted as text. WCAG 1.4.11 (non-text contrast) is the target
    // that applies, not 1.4.3's 4.5:1 for text.
    minContrast: 3,
    contrastLabel: "target for tints",
  },
];

export const APPEARANCE_TOKEN_KEYS: readonly AppearanceTokenKey[] = APPEARANCE_TOKENS.map(
  (t) => t.key,
);

export type AppearanceOverrides = Partial<Record<AppearanceTokenKey, string>>;
export type AppearanceTokens = Record<AppearanceTokenKey, string>;

/* ── colour arithmetic ───────────────────────────────────────────── */

const HEX = /^#[0-9a-f]{6}$/i;

export function isHexColor(value: unknown): value is string {
  return typeof value === "string" && HEX.test(value);
}

type Rgb = [number, number, number];

function fromHex(hex: string): Rgb {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as Rgb;
}

function toHex([r, g, b]: Rgb): string {
  return `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("")}`;
}

/** `color-mix(in srgb, a share, b)` -- the operation the CSS ramps use -- as a hex. */
export function mixHex(a: string, share: number, b: string): string {
  const x = fromHex(a);
  const y = fromHex(b);
  return toHex(x.map((v, i) => v * share + (y[i] ?? 0) * (1 - share)) as Rgb);
}

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = fromHex(hex).map(channel) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  if (!isHexColor(a) || !isHexColor(b)) return 1;
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Black or white, whichever reads better on `background`. */
export function readableInkOn(background: string): string {
  return contrastRatio(background, "#000000") >= contrastRatio(background, "#ffffff")
    ? "#000000"
    : "#ffffff";
}

export type ContrastVerdict = "ok" | "low" | "unreadable";

/**
 * 4.5 is WCAG AA for body text, and the default `target`. A token judged
 * against a lower one -- `accent2`, at WCAG 1.4.11's 3:1 non-text minimum,
 * via `AppearanceTokenDef.minContrast` -- passes `target` explicitly.
 *
 * The "unreadable" floor SCALES WITH `target` rather than staying fixed at
 * 3.0: 3.0 is two thirds of the default 4.5 target, and keeping it flat
 * would erase the "low" band entirely for a 3:1 token (any shortfall at all
 * would jump straight to "unreadable", rather than only a severe one) --
 * so it stays that same fraction of whichever target applies. At the
 * default target this is unchanged: 4.5 * 2/3 = 3.0. Nothing here refuses a
 * colour.
 */
export function contrastVerdict(
  foreground: string,
  background: string,
  target = 4.5,
): ContrastVerdict {
  return verdictForRatio(contrastRatio(foreground, background), target);
}

/** `contrastVerdict`'s three bands for a ratio already measured. */
export function verdictForRatio(ratio: number, target = 4.5): ContrastVerdict {
  if (ratio >= target) return "ok";
  return ratio >= target * (2 / 3) ? "low" : "unreadable";
}

/* ── presets ─────────────────────────────────────────────────────── */

export type ThemePresetId =
  | "light"
  | "dark"
  | "a11y-dark"
  | "a11y-light"
  | "midnight"
  | "ember"
  | "moss"
  | "violet"
  | "daylight"
  | "matrix";

export interface ThemePreset {
  id: ThemePresetId;
  label: string;
  /** What is distinctive about it, in the reader's terms. */
  caption: string;
  /** Which `data-theme` it sits on -- decides `color-scheme` and every dark-only rule. */
  scheme: Scheme;
  /**
   * The preset's own colours, or undefined to take them from the stylesheet.
   * Always a FULL set when present: a partial one would leave the rest coming
   * from whichever base happened to be underneath.
   */
  tokens?: AppearanceTokens;
  /** Ink on a solid accent fill. Computed (black or white) when omitted. */
  onAccent?: string;
  /** One of the two Accessibility Mode presets, which unlock the eleven options. */
  accessibility?: boolean;
}

/**
 * What the stylesheet declares for Light and Dark. Restated ONLY so the
 * customiser can show a value before it is changed and derive from it once it
 * is; `appearance.test.ts` holds these to industry.css / industry-v2.css.
 * Muted and border are the hex a reader actually sees -- the CSS writes them as
 * `color-mix` of the text over the panel.
 */
export const BASE_TOKENS: Record<"light" | "dark", AppearanceTokens> = {
  light: {
    bg: "#e2e4e7",
    surface: "#f8f8f9",
    surface2: "#ffffff",
    text: "#1d1f20",
    muted: mixHex("#1d1f20", 0.76, "#f8f8f9"),
    border: mixHex("#1d1f20", 0.3, "#f8f8f9"),
    // Eden, 24 September 2026 (TRBUD-298): darkened from the shipped #5980a6,
    // which measured 3.26:1 on the page and 3.91:1 on the panel -- both under
    // WCAG AA's 4.5:1 for the links, focus rings and primary button it paints.
    // #486786 clears every pair presetChecks judges (4.63 / 5.56 / 5.90). The
    // old value stays the literal brand blue in two decorative, non-text
    // spots that were deliberately left alone: the favicon's rule
    // (public/favicon.svg) and the map's selection halo
    // (components/FleetMap.tsx SELECTED_HALO) -- see the comments there.
    accent: "#486786",
    accent2: "#728fab",
  },
  dark: {
    bg: "#0b0d0f",
    surface: "#1d2024",
    surface2: "#2a2e33",
    text: "#e6e7e9",
    muted: mixHex("#e6e7e9", 0.76, "#1d2024"),
    border: mixHex("#e6e7e9", 0.34, "#1d2024"),
    accent: "#7ea6cc",
    accent2: "#7e9cb8",
  },
};

export const THEME_PRESETS: readonly ThemePreset[] = [
  { id: "light", label: "Light (default)", caption: "The palette TruckBuddy ships on", scheme: "light" },
  { id: "dark", label: "Dark", caption: "The shipped dark palette", scheme: "dark" },
  {
    // SigmaBuddy's Accessibility Mode surfaces (index.css, `data-theme='eden'`):
    // warm and low-contrast-per-step on purpose, to lower glare rather than put
    // maximum contrast behind every edge on the page.
    id: "a11y-dark",
    label: "Accessibility · dark",
    caption: "Legibility & focus",
    scheme: "dark",
    accessibility: true,
    tokens: {
      bg: "#141517",
      surface: "#1c1d20",
      surface2: "#232427",
      text: "#e8e3d8",
      muted: "#bcb6a8",
      border: "#45484e",
      accent: "#f0a35c",
      accent2: "#8bb8e0",
    },
    onAccent: "#141517",
  },
  {
    // Warm cream rather than a saturated field, which would compete with text.
    id: "a11y-light",
    label: "Accessibility · light",
    caption: "Legibility & focus",
    scheme: "light",
    accessibility: true,
    tokens: {
      bg: "#f0e9dc",
      surface: "#fcf8f0",
      surface2: "#f4ecdd",
      text: "#2e2a24",
      muted: "#5f574b",
      border: "#c3b49a",
      accent: "#954a11",
      accent2: "#335f7e",
    },
    onAccent: "#fcf8f0",
  },
  // The five hue presets SigmaBuddy named after QuoteBuddy's set, with its
  // `info` colour as TruckBuddy's second accent.
  {
    id: "midnight",
    label: "Midnight",
    caption: "Cool blue",
    scheme: "dark",
    tokens: {
      bg: "#0f141b",
      surface: "#171f29",
      surface2: "#1f2935",
      text: "#e7edf3",
      muted: "#93a3b3",
      border: "#2c3947",
      accent: "#8aa0b4",
      accent2: "#7fb0d8",
    },
  },
  {
    id: "ember",
    label: "Ember",
    caption: "Warm red",
    scheme: "dark",
    tokens: {
      bg: "#140f0e",
      surface: "#1e1716",
      surface2: "#271e1c",
      text: "#f2e9e6",
      muted: "#b09a95",
      border: "#3a2c29",
      accent: "#ee8261",
      accent2: "#78a8d4",
    },
  },
  {
    id: "moss",
    label: "Moss",
    caption: "Deep green",
    scheme: "dark",
    tokens: {
      bg: "#0d120f",
      surface: "#151c17",
      surface2: "#1d251f",
      text: "#e8efe9",
      muted: "#94a698",
      border: "#26332a",
      accent: "#6fbf83",
      accent2: "#77aed6",
    },
  },
  {
    id: "violet",
    label: "Violet",
    caption: "Muted purple",
    scheme: "dark",
    tokens: {
      bg: "#120f18",
      surface: "#1a1723",
      surface2: "#231f2e",
      text: "#ece8f3",
      muted: "#a49bb5",
      border: "#332c42",
      accent: "#a68ad9",
      accent2: "#7fadda",
    },
  },
  {
    id: "daylight",
    label: "Daylight",
    caption: "Bright neutral",
    scheme: "light",
    tokens: {
      bg: "#dfe5ea",
      surface: "#f7f9fb",
      surface2: "#e9eef2",
      text: "#141a20",
      muted: "#4a5560",
      border: "#c6d0d9",
      accent: "#1f4e79",
      accent2: "#245a75",
    },
  },
  {
    // Green phosphor on black, a palette you can choose and keep. It began as
    // one of the tab-leave flip's themes; the flip was removed on 23 October
    // 2026 and this preset stays. Surfaces are 5% / 9% ink-into-paper mixes,
    // written out as hex so the customiser can show and edit them. The mono
    // typeface comes from accessibility.css, keyed on `data-palette="matrix"`.
    id: "matrix",
    label: "Matrix",
    caption: "Green phosphor on black",
    scheme: "dark",
    tokens: {
      bg: "#020a03",
      surface: mixHex("#39ff5a", 0.05, "#020a03"),
      surface2: mixHex("#39ff5a", 0.09, "#020a03"),
      text: "#39ff5a",
      muted: mixHex("#39ff5a", 0.76, mixHex("#39ff5a", 0.05, "#020a03")),
      border: mixHex("#39ff5a", 0.34, "#020a03"),
      accent: "#00d43a",
      accent2: "#9dffb0",
    },
    onAccent: "#001a06",
  },
];

export const THEME_PRESET_IDS: readonly ThemePresetId[] = THEME_PRESETS.map((p) => p.id);

export const DEFAULT_THEME_PRESET_ID: ThemePresetId = "light";

export function isThemePresetId(value: unknown): value is ThemePresetId {
  return typeof value === "string" && (THEME_PRESET_IDS as readonly string[]).includes(value);
}

/** The preset with this id, or Light. Total, so a stale stored id is never an error. */
export function presetFor(id: string | null | undefined): ThemePreset {
  return THEME_PRESETS.find((p) => p.id === id) ?? (THEME_PRESETS[0] as ThemePreset);
}

/** Every colour of a preset before any edit: its own tokens, or the stylesheet's. */
export function presetTokens(preset: ThemePreset): AppearanceTokens {
  return preset.tokens ?? BASE_TOKENS[preset.scheme];
}

/** The preset's colours with the person's edits on top. */
export function resolveTokens(
  preset: ThemePreset,
  overrides: AppearanceOverrides | undefined,
): AppearanceTokens {
  return { ...presetTokens(preset), ...sanitizeOverrides(overrides) };
}

/**
 * The two alert hues for each colour scheme (TRBUD-439): red is DOWN, orange is
 * OVERDUE. Restated from industry.css ONLY so `presetChecks` can judge them;
 * `styles/alert-hues.test.ts` holds these to the stylesheet. They are not
 * customiser tokens: a preset keeps its scheme's pair, so a custom page or panel
 * colour can move their contrast, which is what the checks below are for.
 *
 * Light paints each hue as a solid FILL with `ink` on it. Dark (design v6)
 * paints the hue as a 1px hairline and a 16% tint, with `text` (the hue lifted
 * one step) as the label; dark has no use for `ink`, and light has no `text`.
 */
export interface AlertHue {
  hue: string;
  ink: string;
  text: string | null;
}

export const ALERT_TOKENS: Record<Scheme, { down: AlertHue; overdue: AlertHue }> = {
  light: {
    down: { hue: "#b3261e", ink: "#ffffff", text: null },
    overdue: { hue: "#b24d00", ink: "#ffffff", text: null },
  },
  dark: {
    down: { hue: "#f2695c", ink: "#12151a", text: "#f8958a" },
    overdue: { hue: "#f0a23c", ink: "#12151a", text: "#f3ae52" },
  },
};

/** How much of the hue the dark theme's alert tint mixes in (design v6). */
export const DARK_ALERT_TINT = 0.16;

/** One pair `presetChecks` judges: its ratio, the target it is held to, and the verdict. */
export interface PresetCheck {
  ratio: number;
  target: number;
  verdict: ContrastVerdict;
}

function check(ratio: number, target = 4.5): PresetCheck {
  return { ratio, target, verdict: verdictForRatio(ratio, target) };
}

/**
 * Every pair a reader actually looks at, each against its own target. The
 * tests hold every shipped preset to its target on all of them; the customiser
 * only warns.
 *
 * Text pairs are held to 4.5:1. The alert pairs (TRBUD-439; design v2 "Alert
 * hues") are held like the second accent: an alert's EDGE against the page and
 * the panel is a graphic, judged at 3:1 (WCAG 1.4.11), and its LABEL is text at
 * 4.5:1. In a light scheme the edge is the solid fill and the label is the ink
 * on it; in a dark one the edge is the hue's hairline and the label is the
 * lifted text on the hue's 16% tint over the panel.
 */
export function presetChecks(preset: ThemePreset): Record<string, PresetCheck> {
  const t = presetTokens(preset);
  const onAccent = preset.onAccent ?? readableInkOn(t.accent);
  const checks: Record<string, PresetCheck> = {
    "text on page": check(contrastRatio(t.text, t.bg)),
    "text on panel": check(contrastRatio(t.text, t.surface)),
    "text on raised panel": check(contrastRatio(t.text, t.surface2)),
    "muted text on panel": check(contrastRatio(t.muted, t.surface)),
    "links on page": check(contrastRatio(t.accent, t.bg)),
    "links on panel": check(contrastRatio(t.accent, t.surface)),
    "button label on accent": check(contrastRatio(onAccent, t.accent)),
  };
  const alerts = ALERT_TOKENS[preset.scheme];
  for (const kind of ["down", "overdue"] as const) {
    const { hue, ink, text } = alerts[kind];
    checks[`${kind} tag on page`] = check(contrastRatio(hue, t.bg), 3);
    checks[`${kind} tag on panel`] = check(contrastRatio(hue, t.surface), 3);
    checks[`${kind} tag label`] = check(
      text === null
        ? contrastRatio(ink, hue)
        : contrastRatio(text, mixHex(hue, DARK_ALERT_TINT, t.surface)),
    );
  }
  return checks;
}

/* ── storage shapes ──────────────────────────────────────────────── */

export type Appearance = Partial<Record<ThemePresetId, AppearanceOverrides>>;

export function sanitizeOverrides(raw: unknown): AppearanceOverrides {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: AppearanceOverrides = {};
  for (const key of APPEARANCE_TOKEN_KEYS) {
    const value = (raw as Record<string, unknown>)[key];
    if (isHexColor(value)) out[key] = value.toLowerCase();
  }
  return out;
}

/**
 * Runs on data read BACK from storage too: a blob can predate a change to the
 * token list, and a stale key would otherwise reach the DOM.
 */
export function sanitizeAppearance(raw: unknown): Appearance {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Appearance = {};
  for (const id of THEME_PRESET_IDS) {
    const tokens = sanitizeOverrides((raw as Record<string, unknown>)[id]);
    if (Object.keys(tokens).length) out[id] = tokens;
  }
  return out;
}

/* ── the derived property set ────────────────────────────────────── */

const NEUTRAL_STEPS = [
  [100, 10],
  [200, 20],
  [300, 30],
  [400, 42],
  [500, 54],
  [600, 66],
  [700, 78],
  [800, 88],
  [900, 96],
] as const;

function accentRamp(name: string, accent: string, ground: string, ink: string): Record<string, string> {
  return {
    [`--color-${name}-100`]: `color-mix(in srgb, ${accent} 12%, ${ground})`,
    [`--color-${name}-200`]: `color-mix(in srgb, ${accent} 25%, ${ground})`,
    [`--color-${name}-300`]: `color-mix(in srgb, ${accent} 45%, ${ground})`,
    [`--color-${name}-400`]: `color-mix(in srgb, ${accent} 70%, ${ground})`,
    [`--color-${name}-500`]: accent,
    [`--color-${name}-600`]: `color-mix(in srgb, ${accent} 80%, ${ink})`,
    [`--color-${name}-700`]: `color-mix(in srgb, ${accent} 62%, ${ink})`,
    [`--color-${name}-800`]: `color-mix(in srgb, ${accent} 42%, ${ink})`,
    [`--color-${name}-900`]: `color-mix(in srgb, ${accent} 22%, ${ink})`,
  };
}

/**
 * The inline custom properties that paint the active preset with the person's
 * edits, or `{}` when the stylesheet already paints it (Light or Dark, unedited).
 *
 * Muted text and borders follow the TEXT colour, as the stylesheet's own do,
 * until someone sets them -- so changing the text colour alone keeps them
 * coherent instead of leaving them on the old hue.
 */
export function paletteProperties(
  preset: ThemePreset,
  overrides: AppearanceOverrides | undefined,
): Record<string, string> {
  const edits = sanitizeOverrides(overrides);
  if (!preset.tokens && Object.keys(edits).length === 0) return {};

  const t = resolveTokens(preset, edits);
  // A preset's own muted/border are real colours; a base preset's are mixes of
  // its text, so they follow the text unless edited.
  const ownMuted = preset.tokens ? t.muted : edits.muted;
  const ownBorder = preset.tokens ? t.border : edits.border;
  const onAccent =
    edits.accent === undefined && preset.onAccent ? preset.onAccent : readableInkOn(t.accent);

  const props: Record<string, string> = {
    "color-scheme": preset.scheme,
    "--color-bg": t.bg,
    "--color-surface": t.surface,
    "--color-surface-2": t.surface2,
    "--color-inset": `color-mix(in srgb, ${t.bg} 55%, ${t.surface})`,
    "--color-text": t.text,
    "--color-text-muted": ownMuted ?? "color-mix(in srgb, var(--color-text) 76%, transparent)",
    "--color-border-strong":
      ownBorder ?? `color-mix(in srgb, ${t.text} ${preset.scheme === "dark" ? 34 : 30}%, transparent)`,
    "--color-divider": ownBorder
      ? `color-mix(in srgb, ${ownBorder} 70%, transparent)`
      : `color-mix(in srgb, ${t.text} 16%, transparent)`,
    "--color-accent": t.accent,
    "--color-accent-2": t.accent2,
    "--tb-hover-tint": `color-mix(in srgb, ${t.text} 7%, transparent)`,
    "--tb-active-tint": `color-mix(in srgb, ${t.text} 14%, transparent)`,
    "--tb-on-accent": onAccent,
  };
  for (const [step, share] of NEUTRAL_STEPS) {
    props[`--color-neutral-${step}`] = `color-mix(in srgb, ${t.text} ${share}%, ${t.bg})`;
  }
  Object.assign(props, accentRamp("accent", t.accent, t.bg, t.text));
  Object.assign(props, accentRamp("accent-2", t.accent2, t.bg, t.text));
  return props;
}

/** Every property `paletteProperties` can set, so clearing never has to guess. */
export const MANAGED_PROPERTIES: readonly string[] = Object.keys(
  paletteProperties(presetFor("matrix"), undefined),
);

/* ── Accessibility Mode options ──────────────────────────────────── */

/**
 * SigmaBuddy's eleven, in its order. Each key gates a rule in
 * `styles/accessibility.css` through `[data-a11y-opts~="<key>"]`, and the
 * pre-paint script in index.html writes the same attribute from the snapshot.
 */
export const A11Y_OPTION_KEYS = [
  "hyperlegible",
  "spacing",
  "scale",
  "sentence-case",
  "tabular",
  "two-col",
  "compact",
  "targets",
  "no-motion",
  "focus-dim",
  "plain-figures",
] as const;

export type A11yOptionKey = (typeof A11Y_OPTION_KEYS)[number];
export type A11yOptions = Record<A11yOptionKey, boolean>;

/** The hint explains WHY, not just what -- SigmaBuddy's copy, adapted where TruckBuddy differs. */
export const A11Y_OPTION_META: Record<A11yOptionKey, { label: string; hint: string }> = {
  hyperlegible: {
    label: "Hyperlegible typeface",
    hint: "Atkinson Hyperlegible · letterforms that cannot be confused with each other (b/d, I/l/1, rn/m).",
  },
  spacing: {
    label: "Wider letter and word spacing",
    hint: "Adds 1.4% letter and 6% word spacing. The single change with the most reading-speed evidence behind it.",
  },
  scale: {
    label: "Larger base scale",
    hint: "All text steps up 6% together. Spacing is left alone so the live map and the Gantt still line up under the pointer.",
  },
  "sentence-case": {
    label: "Sentence case labels",
    hint: "ALL-CAPS micro-labels lose their word shape; this sets them in sentence case one size up.",
  },
  tabular: {
    label: "Unambiguous, tabular digits",
    hint: "JetBrains Mono with a slashed zero and tabular figures, so columns line up and 0/O never swap.",
  },
  "two-col": {
    label: "Two columns instead of three or more",
    hint: "Card grids cap at two. Not one — a single column on a wide screen empties most of the width and trades comparison for scrolling.",
  },
  compact: {
    label: "Compact spacing",
    hint: "Normal leading in buttons, labels and tables; generous leading kept for running prose.",
  },
  targets: {
    label: "Larger hit targets",
    hint: "Nothing clickable under 34px, and more space between adjacent controls.",
  },
  "no-motion": {
    label: "No incidental motion",
    hint: "Transitions and fades are reduced to nothing.",
  },
  "focus-dim": {
    label: "Focus dimming",
    hint: "Every panel except the one under the pointer or the keyboard steps back to 55% opacity.",
  },
  "plain-figures": {
    label: "Plain-language figures",
    hint: 'Adds a spoken reading under large report figures · "$2,990,000 · about 3 million dollars".',
  },
};

/** Focus dimming is the one option off by default — the most intrusive of the eleven. */
export const DEFAULT_A11Y_OPTIONS: A11yOptions = A11Y_OPTION_KEYS.reduce((acc, key) => {
  acc[key] = key !== "focus-dim";
  return acc;
}, {} as A11yOptions);

export function sanitizeA11yOptions(raw: unknown): A11yOptions {
  const out = { ...DEFAULT_A11Y_OPTIONS };
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const key of A11Y_OPTION_KEYS) {
    const value = (raw as Record<string, unknown>)[key];
    if (typeof value === "boolean") out[key] = value;
  }
  return out;
}

/** The space-separated form the CSS matches with `~=`. */
export function a11yOptionsToAttribute(options: A11yOptions): string {
  return A11Y_OPTION_KEYS.filter((key) => options[key]).join(" ");
}

/* ── plain-language figures ──────────────────────────────────────── */

const MAGNITUDES = [
  [1e9, "billion"],
  [1e6, "million"],
  [1e3, "thousand"],
] as const;

/**
 * A spoken reading of a large figure -- "$2,990,000" -> "about 3 million
 * dollars" -- or null when there is nothing worth saying (small numbers,
 * percentages, anything that does not parse). Returning null rather than a
 * guess is the point: a wrong reading under a right number is worse than none.
 */
export function plainFigureReading(text: string): string | null {
  const trimmed = text.trim();
  const match = trimmed.match(/^(-)?\s*(\$|USD\s*|CAD\s*|MXN\s*)?(-)?\s*([\d,]+(?:\.\d+)?)$/);
  if (!match) return null;
  const negative = Boolean(match[1] || match[3]);
  const currency = (match[2] ?? "").trim();
  const value = Number((match[4] ?? "").replace(/,/g, ""));
  if (!Number.isFinite(value) || value < 1000) return null;
  const [unit, word] = MAGNITUDES.find(([size]) => value >= size) ?? MAGNITUDES[2];
  const scaled = value / unit;
  const rounded = scaled >= 10 ? Math.round(scaled) : Math.round(scaled * 10) / 10;
  const noun =
    currency === "$" || currency === "USD"
      ? " dollars"
      : currency === "CAD"
        ? " Canadian dollars"
        : currency === "MXN"
          ? " pesos"
          : "";
  return `about ${negative ? "minus " : ""}${rounded} ${word}${noun}`;
}
